import { Button, Dialog, DialogHeader, DialogBody, DialogFooter, Alert, Textarea, Radio, Rating } from '@material-tailwind/react';
import { AlertCircleIcon, ArrowRightIcon, PaperclipIcon, RefreshCwIcon, SendIcon } from 'lucide-react';
import moment from 'moment';
import React, { useState } from 'react'
import { useAddSupportTicketInteraction, useAddSupportTicketResolutionAcknowledgment, useGetSupportTicketById, useSubmitSupportTicketFeedback } from '../../custom-hooks/use-support';
import { useNavigate, useParams } from 'react-router-dom';
import { SupportTicketPriorityConfig, SupportTicketStatusConfig } from '../../utils/ui-interpreters';
import { STATIC_USER_ROLES, SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES, SUPPORT_TICKET_STATUSES } from '../../utils/enums';
import { Notify } from '../../utils/functions';

const CustomerSupportDetailPage = ({ handlers }) => {

    const { ticketId } = useParams();
    const navigate = useNavigate();


    const [acknowledgmentDialog, setAcknowledgmentDialog] = useState(false);
    const [acknowledgmentStatus, setAcknowledgmentStatus] = useState(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED);
    const [disputeReason, setDisputeReason] = useState('');
    const [feedbackDialog, setFeedbackDialog] = useState(false);
    const [feedbackRating, setFeedbackRating] = useState(5);
    const [feedbackText, setFeedbackText] = useState('');

    const { data, isLoading: dataLoading, error: dataError } = useGetSupportTicketById(ticketId, 15 * 1000);
    const { ticket, interactions } = data || {};
    const [newMessage, setNewMessage] = useState('');

    const addInteractionMutation = useAddSupportTicketInteraction();
    const addResolutionAcknowledgmentMutation = useAddSupportTicketResolutionAcknowledgment();
    const submitFeedbackMutation = useSubmitSupportTicketFeedback();

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeAgo = (date) => {
        // const now = new Date();
        // const diff = now - new Date(date);
        // const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        // const hours = Math.floor(diff / (1000 * 60 * 60));
        // const minutes = Math.floor(diff / (1000 * 60));

        // if (days > 0) return `${days}d ago`;
        // if (hours > 0) return `${hours}h ago`;
        // if (minutes > 0) return `${minutes}m ago`;
        return moment(date).fromNow();
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;
        try {
            const interaction = {
                message: newMessage,
                interactionType: 'note',
                attachments: []
            };
            await addInteractionMutation.mutateAsync({ ticketId, data: interaction });
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        }
    }

    const handleAcknowledgment = async () => {
        if (acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED && !disputeReason.trim()) {
            Notify("Please provide a reason for disputing the resolution", "error");
            return;
        };
        try {
            const acknowledgment = {
                acknowledgedStatus: acknowledgmentStatus,
                disputeReason: acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED ? disputeReason : null,
                clarificationRequested: acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED ? true : false,
            };
            await addResolutionAcknowledgmentMutation.mutateAsync({ ticketId, data: acknowledgment });
            setAcknowledgmentDialog(false);
            setAcknowledgmentStatus(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED);
            setDisputeReason('');
        } catch (error) {
            console.error('Error sending message:', error);
        }
    }

    const handleSubmitFeedback = async () => {
        try {
            // TODO: Refine the attributes in this api call
            const feedback = {
                satisfactionRating: feedbackRating,
                feedbackText: feedbackText,
                wouldRecommend: feedbackRating > 3 ? 'yes' : 'no',
                improvementSuggestions: feedbackText,
                agentRating: feedbackRating,
                responseTimeRating: feedbackRating,
                resolutionQualityRating: feedbackRating,
            };
            await submitFeedbackMutation.mutateAsync({ ticketId, data: feedback });
            setFeedbackDialog(false);
            setFeedbackRating(5);
            setFeedbackText('');
        } catch (error) {
            console.error('Error submitting feedback:', error);
        }
    }

    if (dataError) {
        return <div>Error: {dataError.message}</div>;
    }

    if (dataLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[40vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                <div className="text-gray-700 text-lg font-medium">Loading ticket details...</div>
                <div className="text-gray-400 text-sm mt-1">Please wait while we fetch your support ticket.</div>
            </div>
        );
    }

    const statusConfig = SupportTicketStatusConfig(ticket.status);
    const priorityConfig = SupportTicketPriorityConfig(ticket.priority);
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto p-6">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="text"
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowRightIcon className="h-4 w-4 rotate-180" />
                        Back to Tickets
                    </Button>
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">{ticket.subject}</h1>
                            <p className="text-gray-600 text-sm"><span className="font-bold uppercase">#TKT-{ticket._id.slice(-5)}</span> • Created {formatDate(ticket.createdDate)}</p>
                        </div>
                        <div className="flex gap-3">
                            <span className={`px-4 py-2 rounded-full text-sm font-medium border ${statusConfig.color}`}>
                                {statusConfig.label}
                            </span>
                            <span className={`px-4 py-2 rounded-full text-sm font-medium border ${priorityConfig.color}`}>
                                {priorityConfig.label}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Ticket Info */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Category</h4>
                            <p className="text-gray-600 capitalize">{ticket.category.replace('_', ' ')}</p>
                        </div>
                        {ticket.assignedAgent && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-2">Assigned Agent</h4>
                                <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                        <span className="text-blue-600 font-semibold text-sm">
                                            {ticket.assignedAgent.name.charAt(0)}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">{ticket.assignedAgent.name}</p>
                                        <p className="text-sm text-gray-600">{ticket.assignedAgent.email}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Last Updated</h4>
                            <p className="text-gray-600">{formatDate(ticket.lastUpdated)}</p>
                        </div>
                    </div>

                    {ticket.resolution && (
                        <div className="pt-6 border-t border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-3">Resolution</h4>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                <p className="text-gray-800 leading-relaxed">{ticket.resolution}</p>
                            </div>

                            {ticket.status === SUPPORT_TICKET_STATUSES.RESOLVED && ticket.requiresCustomerAcknowledgment && (
                                <div className="flex gap-3">
                                    <Button
                                        className="rounded-full"
                                        color="green"
                                        onClick={() => setAcknowledgmentDialog(true)}
                                    >
                                        Accept Resolution
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className="rounded-full"
                                        color="orange"
                                        onClick={() => {
                                            setAcknowledgmentStatus(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED);
                                            setAcknowledgmentDialog(true);
                                        }}
                                    >
                                        Dispute Resolution
                                    </Button>
                                </div>
                            )}
                            {ticket.status === SUPPORT_TICKET_STATUSES.CLOSED && ticket.requiresCustomerAcknowledgment && (
                                <div className="flex justify-end gap-3">
                                    <Button
                                        variant="outlined"
                                        className="rounded-full"
                                        color="blue"
                                        onClick={() => setFeedbackDialog(true)}
                                    >
                                        Leave Feedback
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Action Required Alert */}
                {ticket.requiresCustomerAcknowledgment && ticket.status !== SUPPORT_TICKET_STATUSES.CLOSED && (
                    <Alert color="orange" className="mb-6 border border-orange-200 bg-orange-50 text-orange-700">
                        <AlertCircleIcon className="h-5 w-5" />
                        <h6 className="font-semibold">
                            Action Required
                        </h6>
                        <p className="mt-1">
                            Please review the resolution above and let us know if it resolves your issue.
                        </p>
                    </Alert>
                )}

                {/* Conversation */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Interactions</h3>
                    <div className="space-y-6 mb-6">
                        {interactions.map((interaction) => {
                            const isCustomer = interaction.user.role === STATIC_USER_ROLES.USER;
                            return (
                                <div key={interaction._id} className={`flex ${isCustomer ? 'justify-end' : 'justify-start'} gap-2`}>

                                    <div>
                                        <div className={`max-w-md px-4 py-3 rounded-lg ${isCustomer
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-900 border border-gray-200'
                                            }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className={`h-6 w-6 rounded-full flex items-center justify-center ${isCustomer ? 'bg-blue-500' : 'bg-gray-300'
                                                    }`}>
                                                    <span className={`font-semibold text-xs ${isCustomer ? 'text-white' : 'text-gray-700'
                                                        }`}>
                                                        {interaction.user.name.charAt(0)}
                                                    </span>
                                                </div>
                                                <span className={`text-sm ${isCustomer ? 'text-blue-100' : 'text-gray-600'}`}>
                                                    {interaction.user.name}
                                                </span>
                                                <span className={`text-xs ${isCustomer ? 'text-blue-200' : 'text-gray-500'}`}>

                                                </span>
                                            </div>

                                            <p className="text-sm leading-relaxed">{interaction.message}</p>

                                            {interaction.attachments.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-opacity-20">
                                                    {interaction.attachments.map((attachment, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <PaperclipIcon className="h-4 w-4" />
                                                            <span className="text-sm hover:underline cursor-pointer">
                                                                {attachment.originalName}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 flex justify-between gap-2 px-4">
                                            <div>
                                                {formatDate(interaction.createdDate)}
                                            </div>
                                            <div>
                                                {getTimeAgo(interaction.createdDate)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Reply Box */}
                    {ticket.status !== SUPPORT_TICKET_STATUSES.CLOSED && ticket.status !== SUPPORT_TICKET_STATUSES.CANCELLED && (
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="font-semibold text-gray-900 mb-3">Add a Reply</h4>
                            <div className="space-y-4">
                                <Textarea
                                    label="Message"
                                    placeholder="Type your message here..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    rows={4}
                                />
                                <div className="flex items-center justify-end">
                                    {/* <Button
                                        variant="text"
                                        className="flex items-center gap-2 text-gray-600"
                                    >
                                        <PaperclipIcon className="h-4 w-4" />
                                        Attach Files
                                    </Button> */}
                                    <Button
                                        className="flex items-center gap-2 rounded-full"
                                        color="blue"
                                        size="lg"
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim() || addInteractionMutation.isLoading}
                                    >
                                        {addInteractionMutation.isLoading ? (
                                            <RefreshCwIcon className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <SendIcon className="h-4 w-4 mr-2" />
                                        )}
                                        Send Reply
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Acknowledgment Dialog */}
            <Dialog open={acknowledgmentDialog} handler={() => setAcknowledgmentDialog(false)} className="border border-gray-200">
                <DialogHeader className="border-b border-gray-200">
                    Resolution Response
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-4">
                        <p className="text-gray-700">How would you like to respond to the resolution?</p>

                        <div className="space-y-3">
                            <div className="flex flex-col gap-8">
                                <Radio
                                    name="description"
                                    value={SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED}
                                    checked={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED}
                                    onChange={(e) => setAcknowledgmentStatus(e.target.value)}
                                    label={
                                        <div>
                                            <p className="font-medium">
                                                Accept Resolution
                                            </p>
                                            <p className="font-normal">
                                                The issue has been resolved to my satisfaction
                                            </p>
                                        </div>
                                    }
                                    containerProps={{
                                        className: "-mt-5",
                                    }}
                                />
                                <Radio
                                    name="description"
                                    value={SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                    checked={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                    onChange={(e) => setAcknowledgmentStatus(e.target.value)}
                                    label={
                                        <div>
                                            <p className="font-medium">
                                                Dispute Resolution
                                            </p>
                                            <p className="font-normal">
                                                The issue is not resolved or needs clarification
                                            </p>
                                        </div>
                                    }
                                    containerProps={{
                                        className: "-mt-5",
                                    }}
                                />
                            </div>
                            {/* //comment: Keeping this as an alternative to the radio buttons */}
                            {/* <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                                <input
                                    type="radio"
                                    name="acknowledgment"
                                    value={SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACCEPTED}
                                    checked={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACCEPTED}
                                    onChange={(e) => setAcknowledgmentStatus(e.target.value)}
                                    className="mt-1"
                                />
                                <div>
                                    <p className="font-medium text-gray-900">Accept Resolution</p>
                                    <p className="text-sm text-gray-600">The issue has been resolved to my satisfaction</p>
                                </div>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                                <input
                                    type="radio"
                                    name="acknowledgment"
                                    value={SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                    checked={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                    onChange={(e) => setAcknowledgmentStatus(e.target.value)}
                                    className="mt-1"
                                />
                                <div>
                                    <p className="font-medium text-gray-900">Dispute Resolution</p>
                                    <p className="text-sm text-gray-600">The issue is not resolved or needs clarification</p>
                                </div>
                            </label> */}
                        </div>

                        {acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED && (
                            <Textarea
                                label="Please explain why you're disputing this resolution"
                                required={acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED}
                                value={disputeReason}
                                onChange={(e) => setDisputeReason(e.target.value)}
                                rows={4}
                            />
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className="border-t border-gray-200">
                    <Button
                        variant="text"
                        onClick={() => {
                            setAcknowledgmentDialog(false);
                            setAcknowledgmentStatus(SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACCEPTED);
                            setDisputeReason('');
                        }}
                        className="mr-3 rounded-full"
                        disabled={addResolutionAcknowledgmentMutation.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="rounded-full flex items-center gap-2"
                        color="blue"
                        onClick={handleAcknowledgment}
                        // onClick={handleAcknowledgment}
                        disabled={(acknowledgmentStatus === SUPPORT_TICKET_CUSTOMER_ACKNOWLEDGMENT_STATUSES.DISPUTED && !disputeReason.trim()) || addResolutionAcknowledgmentMutation.isLoading}
                    >
                        {addResolutionAcknowledgmentMutation.isLoading && <RefreshCwIcon className="h-4 w-4 animate-spin mr-2" />}
                        Submit Response
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Feedback Dialog */}
            <Dialog open={feedbackDialog} handler={() => setFeedbackDialog(false)} className="border border-gray-200">
                <DialogHeader className="border-b border-gray-200">
                    <div className="flex flex-col ">
                        <h4 className="font-semibold text-gray-900">Leave Feedback</h4>
                        <p className="text-sm text-gray-500">
                            We value your feedback and will use it to improve our support.
                        </p>
                    </div>
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-gray-900 mb-4">How satisfied are you with the support?</h4>
                            <div className="flex items-center gap-2">
                                <Rating unratedColor="amber" ratedColor="amber" value={feedbackRating} onChange={(value) => setFeedbackRating(value)} />
                                {/* {[1, 2, 3, 4, 5].map((rating) => (
                                    <button
                                        key={rating}
                                        onClick={() => setFeedbackRating(rating)}
                                        className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-lg transition-colors ${rating <= feedbackRating
                                            ? 'bg-yellow-400 border-yellow-400 text-white'
                                            : 'border-gray-300 text-gray-400 hover:border-yellow-400'
                                            }`}
                                    >
                                        ★
                                    </button>
                                ))} */}
                                <span className="ml-3 text-gray-600">
                                    {feedbackRating === 1 && 'Very Poor'}
                                    {feedbackRating === 2 && 'Poor'}
                                    {feedbackRating === 3 && 'Average'}
                                    {feedbackRating === 4 && 'Good'}
                                    {feedbackRating === 5 && 'Excellent'}
                                </span>
                            </div>
                        </div>
                        <div>
                            <Textarea
                                label="Additional Comments (optional)"
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                rows={4}
                                className='mb-0 pb-0'
                            // placeholder="Tell us about your experience..."
                            />
                            <div className='text-xs text-gray-500'>
                                Tell us about your experience...
                            </div>
                        </div>

                    </div>
                </DialogBody>
                <DialogFooter className="border-t border-gray-200">
                    <Button
                        variant="text"
                        onClick={() => {
                            setFeedbackDialog(false);
                            setFeedbackRating(5);
                            setFeedbackText('');
                        }}
                        className="mr-3 text-gray-600 rounded-full"
                        disabled={submitFeedbackMutation.isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="rounded-full flex items-center gap-2"
                        color="blue"
                        onClick={handleSubmitFeedback}
                        disabled={submitFeedbackMutation.isLoading}
                    >
                        {submitFeedbackMutation.isLoading && <RefreshCwIcon className="h-4 w-4 animate-spin mr-2" />}
                        Submit Feedback
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}

export default CustomerSupportDetailPage